import { useState, useCallback, useRef, useEffect, useReducer } from 'react';
import { flushSync } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  createNewChat,
  updateChatTimestamp,
  getChats,
  getChatMessages,
  supportsSSE,
  sendMessageToAGT,
  sendMessageToAGTStream,
  cancelAGTStream,
  editMessage,
  switchVersion,
  getMessageVersions
} from '../../services/AGTChatService';
import {
  uploadAudioFile
} from '../../../AGTHall/services/AGTHallService';


// Función auxiliar para convertir Blob a Base64
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    if (!(blob instanceof Blob)) {
      reject(new Error('Se esperaba un objeto Blob'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = reader.result;
        if (typeof result === 'string' && result.includes('base64,')) {
          resolve(result.split(',')[1]);
        } else {
          reject(new Error('Formato inválido en la conversión'));
        }
      } catch (e) {
        reject(e);
      }
    };
    reader.onerror = (e) => {
      reject(e);
    };
    reader.readAsDataURL(blob);
  });
};

const syncSelectedAppsFromTool = (toolName, toolInput) => {
  if (toolName !== 'UpdateUserPreference' || !toolInput) {
    return;
  }

  if (toolInput.preferenceType !== 'selectedApps' || !toolInput.value) {
    return;
  }

  const { selectedApps, defaultApp } = toolInput.value;
  if (!Array.isArray(selectedApps) || selectedApps.length === 0) {
    return;
  }

  const showUpsInNav = selectedApps.includes('ups');
  const showEduInNav = selectedApps.includes('edu');
  const showFinInNav = selectedApps.includes('fin');
  const showHealthInNav = selectedApps.includes('health');
  const showFitInNav = selectedApps.includes('fit');

  localStorage.setItem('selectedApps', JSON.stringify(selectedApps));
  if (defaultApp) {
    localStorage.setItem('defaultApp', defaultApp);
  }
  localStorage.setItem('showUpsInNav', showUpsInNav.toString());
  localStorage.setItem('showEduInNav', showEduInNav.toString());
  localStorage.setItem('showFinInNav', showFinInNav.toString());
  localStorage.setItem('showHealthInNav', showHealthInNav.toString());
  localStorage.setItem('showFitInNav', showFitInNav.toString());

  window.dispatchEvent(new CustomEvent('appsConfigurationChanged', {
    detail: {
      selectedApps,
      showUpsInNav,
      showEduInNav,
      showFinInNav,
      showHealthInNav,
      showFitInNav
    }
  }));
};

const CHAT_TOOL_USAGE_KEY = 'chatToolUsage';

const trackChatToolUsage = (chatId, toolName, toolInput) => {
  if (!chatId || !toolName) return;
  try {
    const raw = localStorage.getItem(CHAT_TOOL_USAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    const existing = map[chatId] || {};
    const tools = Array.isArray(existing.tools)
      ? existing.tools
      : (existing.toolName ? [{ toolName: existing.toolName, toolInput: existing.toolInput }] : []);
    const alreadyExists = tools.some((tool) => tool.toolName === toolName);
    const nextTools = alreadyExists
      ? tools
      : [...tools, { toolName, toolInput }];
    map[chatId] = {
      ...existing,
      tools: nextTools,
      updatedAt: Date.now()
    };
    localStorage.setItem(CHAT_TOOL_USAGE_KEY, JSON.stringify(map));
    window.dispatchEvent(new CustomEvent('chatToolUsageUpdated', {
      detail: {
        chatId,
        tools: map[chatId].tools
      }
    }));
  } catch (error) {
    // noop
  }
};

const navigateFromTool = (toolName, toolInput, chatId = null) => {
  if (toolName !== 'NavigateToSection' || !toolInput) {
    return;
  }

  const { section } = toolInput;
  const allowedSections = new Set(['home', 'chat', 'fin', 'ups', 'fit', 'health', 'edu', 'profile']);

  if (!section || !allowedSections.has(section)) {
    return;
  }

  if (section === 'fin') {
    localStorage.setItem('agtFinNavigationSource', 'tool');
    if (chatId) {
      localStorage.setItem('agtFinNavigationChatId', chatId);
    }
  }

  window.dispatchEvent(new CustomEvent('navigateToSection', {
    detail: {
      section,
      source: 'tool'
    }
  }));
};

// Definimos un reducer para manejar múltiples actualizaciones de estado relacionadas
const messageStateReducer = (state, action) => {
  switch (action.type) {
    case 'START_WAITING':
      return {
        ...state,
        isWaitingForResponse: true,
        isWaitingForServerResponse: true, // Nuevo estado que indica espera de respuesta del servidor
        isStreaming: false, // Aún no está streaming
        waitingStartTime: Date.now(),
        lastMessageId: action.payload?.lastMessageId || state.lastMessageId,
      };
    case 'START_STREAMING':
      return {
        ...state,
        isStreaming: true,
        isWaitingForServerResponse: false, // Ya recibimos la primera respuesta
      };
    case 'RESPONSE_RECEIVED':
      return {
        ...state,
        isWaitingForServerResponse: false, // La respuesta del servidor llegó
        isWaitingForResponse: false, // También desactivamos isWaitingForResponse inmediatamente
        isStreaming: false, // Streaming terminó
        lastMessageId: null,
        autoScrollActive: false
      };
    case 'END_WAITING':
      return {
        ...state,
        isWaitingForResponse: false,
        isWaitingForServerResponse: false,
        isStreaming: false,
        lastMessageId: null,
        autoScrollActive: false,
      };
    case 'SET_PROCESSING':
      return {
        ...state,
        isProcessingAudio: action.payload.isAudio || false,
        isProcessingImage: action.payload.isImage || false,
      };
    case 'SET_MESSAGE_TYPE_INFO':
      return {
        ...state,
        messageTypeInfo: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload.message,
        retryAction: action.payload.retryAction,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
        retryAction: null,
      };
    case 'SET_RATE_LIMIT':
      return {
        ...state,
        rateLimitInfo: action.payload,
      };
    case 'SET_LAST_IMAGE_REQUEST':
      return {
        ...state,
        lastImageRequestId: action.payload,
      };
    case 'SET_ULTRA_THOUGHTS':
      return {
        ...state,
        ultraThoughts: action.payload,
      };
    case 'SET_LAST_ASSISTANT_MESSAGE':
      return {
        ...state,
        lastAssistantMessage: action.payload,
      };
    case 'SET_AGT_MAKER':
      return {
        ...state,
        isAGTMakerOpen: action.payload.isOpen,
        currentAGTContent: action.payload.content || state.currentAGTContent,
      };
    case 'SET_AGT_MAKER_CALLBACK':
      return {
        ...state,
        agtMakerReadyCallback: action.payload,
      };
    case 'UPDATE_ACTIVE_TOOLS':
      return {
        ...state,
        activeTools: action.payload,
      };
    case 'ADD_TOOL':
      return {
        ...state,
        activeTools: [...state.activeTools, action.payload],
      };
    case 'UPDATE_TOOL_STATUS':
      return {
        ...state,
        activeTools: state.activeTools.map(tool =>
          tool.toolId === action.payload.toolId
            ? {
                ...tool,
                status: action.payload.status,
                error: action.payload.error,
                executionTime: action.payload.executionTime,
                input: action.payload.input,
                phases: action.payload.phases, // Fases de ejecución
                textPositionEnd: action.payload.textPositionEnd // Posición al terminar
              }
            : tool
        ),
      };
    case 'ADD_TOOL_PROGRESS':
      return {
        ...state,
        activeTools: state.activeTools.map(tool =>
          tool.toolId === action.payload.toolId
            ? {
                ...tool,
                phases: [...(tool.phases || []), {
                  phase: action.payload.phase,
                  elapsed: action.payload.elapsed
                }]
              }
            : tool
        ),
      };
    case 'CLEAR_TOOLS':
      return {
        ...state,
        activeTools: [],
      };
    case 'START_VERSION_LOAD':
      return {
        ...state,
        isLoadingVersion: true,
      };
    case 'END_VERSION_LOAD':
      return {
        ...state,
        isLoadingVersion: false,
      };
    default:
      return state;
  }
};

export function useMessageHandling(props) {
  // Extraer todas las props sin sobrescribir valores clave
  const {
    user,
    chatContext,
    setChatContext,
    messages,
    setMessages,
    setVisibleMessages,
    currentChatId,
    setCurrentChatId,
    saveCurrentChatId,
    setIsNewChatView,
    setChats,
    setHasNextPage,
    setLastEvaluatedKey,
    updateChatsOrder,
    setChatTitle,
    projectIdForBackend,
    selectedCategory,
    selectedModel,
    selectedPersonality,
    userName,
    setCachedChat,
    projectContext,
    scrollToBottom,
    messagesEndRef, // Ref para detectar posición de scroll
    onUserPreferencesChanged // Callback cuando el asistente actualiza preferencias
  } = props;

  // Usar useReducer para manejar estados relacionados juntos
  const [messageState, dispatch] = useReducer(messageStateReducer, {
    isWaitingForResponse: false,
    isWaitingForServerResponse: false, // Nuevo estado para separar la espera del servidor de la animación
    isStreaming: false, // Nuevo estado para indicar cuando está streaming
    isLoadingVersion: false, // Estado para indicar cuando se está cargando una versión
    waitingStartTime: null,
    lastMessageId: null,
    lastImageRequestId: null,
    lastAssistantMessage: '',
    ultraThoughts: [],
    error: null,
    retryAction: null,
    rateLimitInfo: null,
    isProcessingAudio: false,
    isProcessingImage: false,
    messageTypeInfo: {
      isAudio: false,
      isImage: false,
      isPdf: false,
      isExcel: false,
      isProjectChat: false,
      modelType: '',
      userIsPro: false
    },
    isAGTMakerOpen: false,
    activeTools: [], // Estado para trackear tools activas
    currentAGTContent: '',
    agtMakerReadyCallback: null,
  });
  
  // Referencias para procesamiento por lotes
  const messageUpdateQueue = useRef([]);
  const isProcessingQueue = useRef(false);
  const userScrollingRef = useRef(false);
  const [autoScrollActive, setAutoScrollActive] = useState(false);

  // Ref para AbortController para cancelar streaming
  const abortControllerRef = useRef(null);

  // Ref para guardar el streamId actual
  const currentStreamIdRef = useRef(null);

// Ref para trackear el estado de streaming de manera síncrona (evita race conditions)
const isStreamingRef = useRef(false);

// Ref para trackear activeTools en tiempo real (evita stale closure)
const activeToolsRef = useRef([]);

// Cache para versiones visitadas (optimistic updates al navegar entre versiones)
// Estructura: { "chatId-baseMessageId-versionIndex": { activeBranch, context } }
const versionsCacheRef = useRef(new Map());

// Fusionar mensajes del backend preservando uiId para evitar re-montajes que causen parpadeos
const mergeMessagesWithUIIds = (currentMessages = [], activeMessages = []) => {
  return activeMessages.map((msg, idx) => {
    const current = currentMessages[idx];
    const uiId = current?.uiId || current?.id || msg.id;

    // Si no hay cambios de texto/ID/isUser, reutilizamos la referencia actual para evitar re-render global
    const noChange =
      current &&
      current.text === msg.text &&
      current.id === msg.id &&
      current.isUser === msg.isUser &&
      current.wasCancelled === msg.wasCancelled;

    if (noChange) {
      return current;
    }

    return { ...msg, uiId };
  });
};

// Extraer estados del reducer para acceso más fácil
const {
  isWaitingForResponse,
  isWaitingForServerResponse, // Nuevo estado extraído
    isStreaming, // Nuevo estado para indicar streaming activo
    isLoadingVersion, // Estado para indicar si se está cargando una versión
    waitingStartTime,
    lastMessageId,
    lastImageRequestId,
    lastAssistantMessage,
    ultraThoughts,
    activeTools, // Estado de tools en ejecución
    error,
    retryAction,
    rateLimitInfo,
    isProcessingAudio,
    isProcessingImage,
    messageTypeInfo,
    isAGTMakerOpen,
    currentAGTContent,
    agtMakerReadyCallback,
  } = messageState;

  // Sincronizar activeTools con la ref para evitar stale closure
  useEffect(() => {
    activeToolsRef.current = activeTools;
    console.log('🔄 [Frontend] activeTools actualizado:', activeTools);
  }, [activeTools]);

  const ensureToolTracked = (toolId, toolName, status, textPositionStart) => {
    if (!toolId) return;
    const alreadyTracked = activeToolsRef.current.some(tool => tool.toolId === toolId);
    if (alreadyTracked) return;

    const payload = {
      toolId,
      toolName,
      status,
      textPositionStart,
      marker: `__TOOL_MARKER_${toolId}__`
    };

    activeToolsRef.current = [...activeToolsRef.current, payload];
    dispatch({ type: 'ADD_TOOL', payload });
  };

  // Sincronizar el ref con el estado de streaming (evita race conditions en edición)
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Helper para finalizar streaming (actualiza ref + dispatch)
  const endStreamingState = useCallback(() => {
    isStreamingRef.current = false;
    dispatch({ type: 'END_WAITING' });
  }, []);

  // Handle AGT Tag detection
  const hasAGTTag = useCallback((content) => {
    if (!content) return false;
    try {
      const parsed = JSON.parse(content);
      if (parsed.text) {
        return typeof parsed.text === 'string' && parsed.text.includes('<agt>');
      }
      return typeof parsed === 'string' && parsed.includes('<agt>');
    } catch {
      return typeof content === 'string' && content.includes('<agt>');
    }
  }, []);

  // Actualizar mensaje en lotes (batch)
  const handleUpdateMessage = useCallback((messageId, newContent) => {
    messageUpdateQueue.current.push({ messageId, newContent });
    if (!isProcessingQueue.current) {
      isProcessingQueue.current = true;
      requestAnimationFrame(() => {
        const updates = [...messageUpdateQueue.current];
        messageUpdateQueue.current = [];

        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          updates.forEach((update) => {
            const index = newMessages.findIndex((msg) => msg.id === update.messageId);
            if (index !== -1) {
              newMessages[index] = {
                ...newMessages[index],
                text: update.newContent,
              };
            }
          });
          return newMessages;
        });

        setVisibleMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          updates.forEach((update) => {
            const index = newMessages.findIndex((msg) => msg.id === update.messageId);
            if (index !== -1) {
              newMessages[index] = {
                ...newMessages[index],
                text: update.newContent,
              };
            }
          });
          return newMessages;
        });

        isProcessingQueue.current = false;
      });
    }
  }, [setMessages, setVisibleMessages]);

  // Manejo de apertura de AGTMaker
  const handleOpenAGTMaker = useCallback((content, readyCallback) => {
    dispatch({
      type: 'SET_AGT_MAKER',
      payload: { isOpen: true, content }
    });
    dispatch({
      type: 'SET_AGT_MAKER_CALLBACK',
      payload: readyCallback
    });
  }, []);

  const handleCloseAGTMaker = useCallback(() => {
    dispatch({
      type: 'SET_AGT_MAKER',
      payload: { isOpen: false, content: '' }
    });
    dispatch({
      type: 'SET_AGT_MAKER_CALLBACK',
      payload: null
    });
  }, []);

  const handleAGTMakerContentReady = useCallback(() => {
    if (agtMakerReadyCallback) {
      agtMakerReadyCallback();
      dispatch({
        type: 'SET_AGT_MAKER_CALLBACK',
        payload: null
      });
    }
  }, [agtMakerReadyCallback]);

  // Manejo de rate-limit 
  useEffect(() => {
    if (rateLimitInfo) {
      const timer = setTimeout(() => {
        dispatch({ type: 'SET_RATE_LIMIT', payload: null });
      }, rateLimitInfo.resetIn * 1000);
      return () => clearTimeout(timer);
    }
  }, [rateLimitInfo]);

  // Función para procesar mensajes de audio
  const processAudioMessage = useCallback(async (messageData) => {
    const isAudioMessage = typeof messageData.text === 'object' && 
                          (messageData.text.type === 'audio_message' || 
                          messageData.text.audioBlob || 
                          messageData.text.audioBase64);
   
    if (!isAudioMessage) {
      return null; // No es un mensaje de audio, continuar con procesamiento normal
    }
   
    const audioData = messageData.text;
   
    try {
      // Determinar qué tenemos disponible
      let audioBlob = null;
      let audioBase64 = null;
      let audioS3Url = null;
   
      // PRIMERO: Verificar si ya tenemos una URL de S3 válida (máxima prioridad)
      audioS3Url = audioData.audioS3Url || audioData.audioUrl;
   
      // IMPORTANTE: Verificar que no sea una URL de blob local
      if (audioS3Url && audioS3Url.startsWith('blob:')) {
        audioS3Url = null; // Resetear para que se procese como blob
      }
   
      if (audioS3Url && (audioS3Url.includes('s3.amazonaws.com') || audioS3Url.includes('cloudfront.net'))) {
        // Si tenemos URL pero igual enviamos el audio, guardar una referencia a bucket y key si existen
        const s3Key = audioData.s3Key;
        const bucket = audioData.bucket;
   
        // Si tenemos URL, no necesitamos subir de nuevo, retornamos ahora
        return {
          type: 'audio_message',
          text: audioData.text || '',
          audioS3Url: audioS3Url,
          audioUrl: audioS3Url,
          s3Key,
          bucket,
          duration: audioData.duration || 0,
          timestamp: new Date().toISOString(),
          transcription: audioData.text || audioData.transcription || '',
          voicePreference: messageData.voicePreference || 'none',
          source: messageData.source || 'web',
          captureType: messageData.captureType || 'recorder'
        };
      }
   
      // SEGUNDO: Si tenemos una URL de blob, intentar obtener el blob
      if (audioData.audioUrl && audioData.audioUrl.startsWith('blob:')) {
        try {
          const response = await fetch(audioData.audioUrl);
          if (response.ok) {
            audioBlob = await response.blob();
          }
        } catch (fetchError) {
          // Error handling is silent
        }
      }
   
      // TERCERO: Si tenemos un blob directo, lo usamos
      if (!audioBlob && audioData.audioBlob instanceof Blob) {
        audioBlob = audioData.audioBlob;
      }
   
      // CUARTO: Si tenemos Base64, lo usamos
      if (audioData.audioBase64) {
        audioBase64 = audioData.audioBase64;
      } else if (audioBlob) {
        // Convertir el Blob a Base64 si no tenemos ya Base64
        try {
          audioBase64 = await blobToBase64(audioBlob);
        } catch (convError) {
          // Error handling is silent
        }
      }
   
      // QUINTO: SIEMPRE subir a S3 si tenemos blob o base64
      if (audioBlob || audioBase64) {
        try {
          // Usar el blob directamente si está disponible
          const uploadResult = await uploadAudioFile(audioBlob || audioBase64, user.uid);
          audioS3Url = uploadResult.audioS3Url || uploadResult.audioUrl || uploadResult.signedUrl;
   
          // Capturar la bucket y s3Key para futuras referencias
          const s3Key = uploadResult.s3Key;
          const bucket = uploadResult.bucket;
   
          // Construir el mensaje con todos los datos que tenemos
          return {
            type: 'audio_message',
            text: audioData.text || '',
            audioBase64, // Para el backend, por si acaso
            audioS3Url,  // Para la reproducción en frontend
            audioUrl: audioS3Url, // Duplicado por compatibilidad
            s3Key,      // Importante para regenerar la URL
            bucket,     // Importante para regenerar la URL
            duration: audioData.duration || 0,
            timestamp: new Date().toISOString(),
            voicePreference: messageData.voicePreference || 'none',
            source: messageData.source || 'web',
            captureType: messageData.captureType || 'recorder',
            transcription: audioData.text || audioData.transcription || ''
          };
        } catch (uploadError) {
          // Continue with error message
        }
      }
   
      // Si llegamos aquí es porque hubo un error o no tenemos datos de audio válidos
      return {
        type: 'text',
        text: audioData.text || '[Error: No se pudo procesar el audio]',
        voicePreference: messageData.voicePreference || 'none',
        source: messageData.source || 'web',
        captureType: messageData.captureType || 'recorder',
        error: true
      };
    } catch (error) {
      return {
        text: `[Error de audio] ${audioData.text || 'No se pudo procesar el audio'}`,
        voicePreference: messageData.voicePreference || 'none',
        source: messageData.source || 'web',
        error: true
      };
    }
  }, [user.uid]);

  // IMPORTANTE: Definir handleNewChatInput primero para evitar error de referencia

  // Crear un nuevo chat y enviar el primer mensaje
  const handleNewChatInput = useCallback(
    async (messageData) => {
      if (!user || !user.uid || isWaitingForResponse) return;
  
      const isValidMessage =
        typeof messageData.text === 'object' &&
        messageData.text.type === 'audio_message'
          ? true
          : typeof messageData.text === 'string' &&
            messageData.text.trim().length > 0;
  
      if (!isValidMessage && !messageData.files?.length) return;
  
      // Actualizar estados de espera en un solo dispatch
      dispatch({ 
        type: 'START_WAITING', 
        payload: { lastMessageId: null } 
      });

      // Detectar tipos de archivos
      const isAudioMessage =
        typeof messageData.text === 'object' &&
        messageData.text.type === 'audio_message';
      const hasImageFile = messageData.files?.some((file) =>
        file.type.startsWith('image/')
      );
      const hasPdf = messageData.files?.some((file) => 
        file.type === 'application/pdf'
      );
      const hasExcel = messageData.files?.some((file) =>
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel'
      );
  
      // Actualizar estados de procesamiento en un solo dispatch
      dispatch({ 
        type: 'SET_PROCESSING', 
        payload: { isAudio: isAudioMessage, isImage: hasImageFile } 
      });
      
      // Actualizar info de tipo de mensaje en un solo dispatch
      dispatch({
        type: 'SET_MESSAGE_TYPE_INFO',
        payload: {
          isAudio: isAudioMessage,
          isImage: hasImageFile,
          isPdf: hasPdf,
          isExcel: hasExcel,
          isProjectChat: !!projectContext,
          modelType: selectedModel || 'agt-nest-20250314',
          userIsPro: user?.isPro,
          // Deprecated: Only AGT Nest tier available now
          modelTier: 'agt'
        }
      });
  
      try {
        // 1) Creamos el nuevo chat en backend
        const chatCategory = projectContext
          ? projectContext.category
          : selectedCategory !== 'add'
          ? selectedCategory
          : 'personal';

        const newChat = await createNewChat(
          user.uid,
          chatCategory,
          projectIdForBackend
        );
        
        if (!newChat || !newChat.id) {
          throw new Error('Failed to create new chat');
        }
  
        // Guardamos la ID localmente
        const chatIdToSend = newChat.id;
        setCurrentChatId(chatIdToSend);
        saveCurrentChatId(chatIdToSend);
        setChatTitle('New chat');

        // Actualizar lista de chats local sin refetch (evita parpadeo)
        setChats((prevChats) => {
          const newChatList = [
            {
              ...newChat,
              name: 'New chat',
              title: 'New chat',
              updatedAt: new Date().toISOString(),
            },
            ...prevChats,
          ];
          return newChatList;
        });
        
        // Opcional: actualizar flags de paginación si existen
        if (typeof setHasNextPage === 'function') {
          setHasNextPage((prev) => prev ?? false);
        }
        
        if (typeof setLastEvaluatedKey === 'function') {
          setLastEvaluatedKey((prev) => prev ?? null);
        }
        
        updateChatsOrder();
        
        // Procesar mensaje de audio si es necesario
        let processedMessage = null;
        if (isAudioMessage) {
          try {
            processedMessage = await processAudioMessage(messageData);
          } catch (error) {
            // Error handling is silent
          }
        }
  
        // 2) Agregar el primer mensaje a la UI
        const userUiId = uuidv4();
        const newMessage = {
          id: userUiId,
          uiId: userUiId,
          text: processedMessage 
            ? JSON.stringify({
                ...processedMessage
              }) 
            : typeof messageData.text === 'object'
              ? JSON.stringify({
                  ...messageData.text,
                  source: messageData.source,
                  captureType: messageData.captureType,
                })
              : messageData.text,
          isUser: true,
          timestamp: Date.now(),
          // MEJORADO: Incluir TODOS los campos relevantes a nivel raíz
          audioS3Url: processedMessage?.audioS3Url,
          audioUrl: processedMessage?.audioS3Url, // También como audioUrl para compatibilidad
          s3Key: processedMessage?.s3Key,  // NUEVO: Incluir s3Key si está disponible
          bucket: processedMessage?.bucket, // NUEVO: Incluir bucket si está disponible
          duration: processedMessage?.duration,
          transcription: processedMessage?.transcription,
          files: messageData.files?.map((file) => ({
            ...file,
            source: messageData.source || file.source,
          })),
          voicePreference: messageData.voicePreference,
          imageBase64Data: messageData.files,
          source: messageData.source,
          captureType: messageData.captureType
        };
  
        setMessages((prev) => [...prev, newMessage]);
        setVisibleMessages((prev) => [...prev, newMessage]);
        setIsNewChatView(false);
  
        requestAnimationFrame(() => {
          scrollToBottom({ force: true });
        });
  
        // Preparar mensaje de respuesta vacío
        const responseUiId = uuidv4();
        const responseMessage = {
          id: responseUiId,
          uiId: responseUiId,
          text: '',
          isUser: false,
          timestamp: Date.now(),
          imageBase64Data: null,
          source: messageData.source,
          captureType: messageData.captureType
        };

        // Actualizar lastMessageId en un solo dispatch
        dispatch({
          type: 'START_WAITING',
          payload: { lastMessageId: responseMessage.id }
        });

        // STREAMING HABILITADO PARA TODOS LOS MODELOS
        if (supportsSSE()) {
          // Inicializar array de pensamientos (vacío para modelos que no son Ultra)
          dispatch({ type: 'SET_ULTRA_THOUGHTS', payload: [] });

          // CRÍTICO: Agregar el mensaje placeholder ANTES de iniciar el streaming
          flushSync(() => {
            setMessages((prev) => [...prev, responseMessage]);
            setVisibleMessages((prev) => [...prev, responseMessage]);
          });

          // Preparar el mensaje para enviar
          const finalMessage = processedMessage || {
            text: messageData.text,
            files: messageData.files,
            voicePreference: messageData.voicePreference,
            source: messageData.source,
            captureType: messageData.captureType,
            projectId: projectIdForBackend,
            metadata: {
              source: messageData.source,
              captureType: messageData.captureType,
              deviceInfo: 'web',
              timestamp: new Date().toISOString()
            },
          };

          // Enviar mensaje con streaming
          let streamingText = '';
          let wasNearBottom = true; // Tracking si el usuario estaba cerca del fondo

          // Función helper para verificar si el usuario está cerca del fondo
          // (igual que ChatGPT/Claude.ai)
          const isUserNearBottom = () => {
            const container = messagesEndRef?.current?.parentElement;
            if (!container) return true;

            const threshold = 150; // px desde el fondo (igual que ChatGPT)
            const position = container.scrollHeight - container.scrollTop - container.clientHeight;
            return position < threshold;
          };

          // Verificar posición inicial
          wasNearBottom = isUserNearBottom();

          // Crear AbortController para poder cancelar el streaming
          abortControllerRef.current = new AbortController();

          // Marcar que el streaming ha comenzado (antes de la llamada)
          isStreamingRef.current = true; // Actualizar ref inmediatamente
          dispatch({ type: 'START_STREAMING' });

          const response = await sendMessageToAGTStream(
            finalMessage,
            user.uid,
            chatIdToSend,
            {}, // No contexto previo
            selectedModel || 'agt-nest-20250314',
            selectedPersonality || 'normal',
            userName,
            user?.email || '',
            messageData.voicePreference,
            // onToken callback - se llama cada vez que llega un token
            (token) => {
              // Si es el streamId, guardarlo y retornar
              if (typeof token === 'object' && token.type === 'stream_id') {
                currentStreamIdRef.current = token.streamId;
                return;
              }

              // Manejar eventos de tools
              if (typeof token === 'object') {
                console.log('🔍 [Frontend] Token recibido:', token);
                switch (token.type) {
                  case 'tool_marker':
                    console.log('📍📍📍 [Frontend] MARCADOR RECIBIDO:', token.marker, 'para', token.toolName);
                    console.log('📍 [Frontend] streamingText antes:', streamingText.substring(0, 100));

                    // VALIDACIÓN: Asegurar que token.marker es un string
                    if (typeof token.marker !== 'string') {
                      console.error('⚠️ [Frontend] token.marker NO es string:', {
                        type: typeof token.marker,
                        value: token.marker,
                        fullToken: token
                      });
                      return;
                    }

                    ensureToolTracked(token.toolId, token.toolName, 'initializing', streamingText.length);

                    // Inyectar el marcador en el texto del stream
                    streamingText += `\n${token.marker}\n`;
                    console.log('📍 [Frontend] streamingText después:', streamingText.substring(0, 200));

                    // Actualizar el mensaje en tiempo real con el marcador
                    setMessages((prev) => {
                      const newMessages = [...prev];
                      const lastIndex = newMessages.length - 1;
                      if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                        newMessages[lastIndex] = {
                          ...newMessages[lastIndex],
                          text: streamingText
                        };
                      }
                      return newMessages;
                    });

                    setVisibleMessages((prev) => {
                      const newMessages = [...prev];
                      const lastIndex = newMessages.length - 1;
                      if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                        newMessages[lastIndex] = {
                          ...newMessages[lastIndex],
                          text: streamingText
                        };
                      }
                      return newMessages;
                    });
                    return;

                  case 'tool_start':
                    console.log('🔧 [Frontend] Tool start:', token.toolName, `(posición: ${streamingText.length})`);
                    dispatch({
                      type: 'ADD_TOOL',
                      payload: {
                        toolId: token.toolId,
                        toolName: token.toolName,
                        status: 'initializing', // Estado inicial: preparando
                        input: token.input,
                        textPositionStart: streamingText.length, // Guardar posición cuando inicia
                        marker: `__TOOL_MARKER_${token.toolId}__` // Guardar el marcador
                      }
                    });
                    return;

                  case 'tool_executing':
                    console.log('⚙️ [Frontend] Tool executing:', token.toolName);
                    ensureToolTracked(token.toolId, token.toolName, 'running', streamingText.length);
                    dispatch({
                      type: 'UPDATE_TOOL_STATUS',
                      payload: {
                        toolId: token.toolId,
                        status: 'running' // Ahora está ejecutándose
                      }
                    });
                    return;

                  case 'tool_progress':
                    console.log('📍 [Frontend] Tool progress:', token.toolName, '-', token.phase);
                    ensureToolTracked(token.toolId, token.toolName, 'running', streamingText.length);
                    dispatch({
                      type: 'ADD_TOOL_PROGRESS',
                      payload: {
                        toolId: token.toolId,
                        phase: token.phase,
                        elapsed: token.elapsed
                      }
                    });
                    return;

                  case 'tool_complete':
                    console.log('✅ [Frontend] Tool complete:', token.toolName, `(${token.executionTime}ms, posición: ${streamingText.length})`);
                    ensureToolTracked(token.toolId, token.toolName, 'completed', streamingText.length);
                    dispatch({
                      type: 'UPDATE_TOOL_STATUS',
                      payload: {
                        toolId: token.toolId,
                        status: 'completed',
                        executionTime: token.executionTime,
                        input: token.input,
                        phases: token.phases,
                        textPositionEnd: streamingText.length // Guardar posición cuando termina
                      }
                    });
                    trackChatToolUsage(chatIdToSend || currentChatId, token.toolName, token.input);
                    syncSelectedAppsFromTool(token.toolName, token.input);
                    navigateFromTool(token.toolName, token.input, chatIdToSend || currentChatId);
                    return;

                  case 'tool_error':
                    console.error('❌ [Frontend] Tool error:', token.toolName, token.error);
                    ensureToolTracked(token.toolId, token.toolName, 'error', streamingText.length);
                    dispatch({
                      type: 'UPDATE_TOOL_STATUS',
                      payload: {
                        toolId: token.toolId,
                        status: 'error',
                        error: token.error,
                        textPositionEnd: streamingText.length // Guardar posición cuando falla
                      }
                    });
                    return;

                  case 'executing_tools':
                    console.log(`⚙️ [Frontend] Ejecutando ${token.count} tool(s)`);
                    return;

                  case 'continuing_stream':
                    console.log('🔄 [Frontend] Continuando stream con resultados');
                    return;

                  default:
                    // Si es un objeto pero no es un tipo conocido, no agregarlo al texto
                    console.warn('⚠️ [Frontend] Token objeto desconocido:', token);
                    return;
                }
              }

              // Solo agregar al texto si es un string
              if (typeof token === 'string') {
                streamingText += token;
              } else {
                console.warn('⚠️ [Frontend] Token no es string ni objeto conocido:', token);
                return;
              }

              // Actualizar el mensaje del asistente en tiempo real
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                  newMessages[lastIndex] = {
                    ...newMessages[lastIndex],
                    text: streamingText
                  };
                }
                return newMessages;
              });

              setVisibleMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                  newMessages[lastIndex] = {
                    ...newMessages[lastIndex],
                    text: streamingText
                  };
                }
                return newMessages;
              });

              // Auto-scroll SOLO si el usuario estaba cerca del fondo
              // (comportamiento idéntico a ChatGPT/Claude.ai)
              if (wasNearBottom) {
                // Esperar a que React actualice el DOM
                requestAnimationFrame(() => {
                  scrollToBottom({ force: false });

                  // Actualizar el estado después del scroll
                  // Si el usuario scrollea manualmente hacia arriba, se detectará en el siguiente token
                  setTimeout(() => {
                    wasNearBottom = isUserNearBottom();
                  }, 50);
                });
              } else {
                // Verificar periódicamente si el usuario volvió al fondo
                setTimeout(() => {
                  wasNearBottom = isUserNearBottom();
                }, 50);
              }
            },
            // onDone callback - se llama cuando termina el streaming
            (result) => {
              console.log('🏁 [Frontend] onDone - iniciando finalización del stream');

              // Marcar streaming finalizado antes de cualquier dispatch/render
              isStreamingRef.current = false;

              // CRÍTICO: Guardar snapshot de tools INMEDIATAMENTE antes de cualquier otro update
              const toolsSnapshot = [...activeToolsRef.current];
              console.log('💾 [Frontend] Guardando tools en mensaje:', JSON.stringify(toolsSnapshot, null, 2));
              console.log('💾 [Frontend] Número de tools:', toolsSnapshot.length);

              // Actualizar el mensaje del asistente con las tools usadas INMEDIATAMENTE
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                  const updatedMessage = {
                    ...newMessages[lastIndex],
                    toolsUsed: toolsSnapshot.length > 0 ? toolsSnapshot : undefined
                  };
                  console.log('📝 [Frontend] Mensaje actualizado con tools:', {
                    messageId: updatedMessage.id,
                    toolsUsed: updatedMessage.toolsUsed,
                    toolsCount: toolsSnapshot.length
                  });
                  newMessages[lastIndex] = updatedMessage;
                }
                return newMessages;
              });

              setVisibleMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                  const updatedMessage = {
                    ...newMessages[lastIndex],
                    toolsUsed: toolsSnapshot.length > 0 ? toolsSnapshot : undefined
                  };
                  newMessages[lastIndex] = updatedMessage;
                }
                return newMessages;
              });

              // Limpiar activeTools DESPUÉS de guardar en el mensaje
              dispatch({ type: 'CLEAR_TOOLS' });
              console.log('🧹 [Frontend] activeTools limpiados después de guardar');

              // Actualizar contexto
              if (result.updatedContext) {
                setChatContext(result.updatedContext);
              }

              // Scroll final forzado (asegurar que se vea el mensaje completo)
              requestAnimationFrame(() => {
                scrollToBottom({ force: true });
              });

              // IMPORTANTE: Indicar que se recibió respuesta del servidor
              dispatch({ type: 'RESPONSE_RECEIVED' });
              dispatch({ type: 'END_WAITING' });
            },
            // onError callback
            (error) => {
              console.error('[STREAMING] ❌ Error:', error);

              // Si el error es por cancelación (abort), guardar el mensaje parcial
              if (error.name === 'AbortError') {

                // Actualizar el mensaje con el texto parcial que teníamos
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastIndex = newMessages.length - 1;
                  if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                    newMessages[lastIndex] = {
                      ...newMessages[lastIndex],
                      text: streamingText,
                      wasCancelled: true, // Marcar que fue cancelado
                    };
                  }
                  return newMessages;
                });

                setVisibleMessages((prev) => {
                  const newMessages = [...prev];
                  const lastIndex = newMessages.length - 1;
                  if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                    newMessages[lastIndex] = {
                      ...newMessages[lastIndex],
                      text: streamingText,
                      wasCancelled: true,
                    };
                  }
                  return newMessages;
                });

                dispatch({ type: 'END_WAITING' });

                // CRÍTICO: Refrescar mensajes desde el backend para obtener IDs correctos
                // Esto es necesario para que los botones de editar/reenviar funcionen después de cancelar
                updateChatTimestamp(user.uid, chatIdToSend)
                  .then(() => getChatMessages(user.uid, chatIdToSend))
                  .then((freshMessages) => {
                    if (freshMessages && freshMessages.length > 0) {
                      const activeMessages = freshMessages.filter(msg => msg.isActive !== false);
                      setMessages(activeMessages);
                      setVisibleMessages(activeMessages);
                    }
                  })
                  .catch((refreshError) => {
                    console.error('[STREAMING] ⚠️ Error refrescando mensajes:', refreshError);
                    // No fallar si no se pueden refrescar los mensajes
                  });

                return;
              }

              dispatch({ type: 'END_WAITING' });
              dispatch({
                type: 'SET_ERROR',
                payload: {
                  message: error.message || 'Error al procesar el mensaje',
                  retryAction: null
                }
              });
            },
            abortControllerRef.current?.signal,
            // onPreferencesUpdated callback
            (updatedPreferences) => {
              console.log('🔔 [HOOK] Preferencias actualizadas por el asistente:', updatedPreferences);
              if (onUserPreferencesChanged) {
                onUserPreferencesChanged(updatedPreferences);
              }
            }
          );

          // Capturar y registrar URL de audio si existe
          const audioUrl = response.audioUrl || response.audioS3Url;
          const s3Key = response.s3Key;
          const bucket = response.bucket;

          // Actualizar respuesta
          dispatch({
            type: 'SET_LAST_ASSISTANT_MESSAGE',
            payload: {
              text: response.reply || streamingText,
              audioBlob: response.audioBlob,
              audioUrl,
              audioS3Url: audioUrl,
              s3Key,
              bucket,
              isUser: false,
            }
          });

          // IMPORTANTE: NO reemplazar el mensaje completo, solo actualizar campos necesarios
          // para evitar re-render que causa pestañeo
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastIndex = newMessages.length - 1;
            if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
              const previousMessage = newMessages[lastIndex];

              // CRÍTICO: Si el mensaje anterior tiene marcadores, NO reemplazar el texto
              // porque response.reply del backend NO contiene los marcadores
              const hasMarkers = previousMessage.text?.includes('__TOOL_MARKER_');
              const shouldKeepPreviousText = hasMarkers && response.reply && !response.reply.includes('__TOOL_MARKER_');

              // SOLO actualizar campos que podrían haber cambiado, preservando el resto
              newMessages[lastIndex] = {
                ...previousMessage, // ← Preservar TODO del mensaje anterior
                text: shouldKeepPreviousText ? previousMessage.text : (response.reply || previousMessage.text),
                audioUrl: audioUrl || previousMessage.audioUrl,
                audioS3Url: audioUrl || previousMessage.audioS3Url,
                s3Key: s3Key || previousMessage.s3Key,
                bucket: bucket || previousMessage.bucket,
                duration: response.duration || previousMessage.duration || 0,
                // toolsUsed ya está en previousMessage, no hace falta preservar
              };
              console.log('🔧 [Frontend] Actualizando mensaje sin reemplazar (evitar pestañeo):', {
                messageId: newMessages[lastIndex].id,
                toolsUsed: newMessages[lastIndex].toolsUsed,
                hasMarkers: newMessages[lastIndex].text?.includes('__TOOL_MARKER_'),
                preservedText: shouldKeepPreviousText
              });
            }
            return newMessages;
          });
          setVisibleMessages((prev) => {
            const newMessages = [...prev];
            const lastIndex = newMessages.length - 1;
            if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
              const previousMessage = newMessages[lastIndex];

              // CRÍTICO: Si el mensaje anterior tiene marcadores, NO reemplazar el texto
              const hasMarkers = previousMessage.text?.includes('__TOOL_MARKER_');
              const shouldKeepPreviousText = hasMarkers && response.reply && !response.reply.includes('__TOOL_MARKER_');

              // SOLO actualizar campos que podrían haber cambiado, preservando el resto
              newMessages[lastIndex] = {
                ...previousMessage, // ← Preservar TODO del mensaje anterior
                text: shouldKeepPreviousText ? previousMessage.text : (response.reply || previousMessage.text),
                audioUrl: audioUrl || previousMessage.audioUrl,
                audioS3Url: audioUrl || previousMessage.audioS3Url,
                s3Key: s3Key || previousMessage.s3Key,
                bucket: bucket || previousMessage.bucket,
                duration: response.duration || previousMessage.duration || 0,
              };
            }
            return newMessages;
          });

          // Crear assistantMessage SOLO para el caché, usando los datos actualizados
          const assistantMessage = {
            ...responseMessage,
            uiId: responseMessage.uiId || responseMessage.id,
            text: response.reply || streamingText,
            audioUrl,
            audioS3Url: audioUrl,
            s3Key,
            bucket,
            duration: response.duration || 0,
            // Incluir toolsUsed en el caché si está disponible
            toolsUsed: undefined // El caché no necesita toolsUsed
          };

          // Actualizar contexto
          setChatContext(response.updatedContext || {});
          setCachedChat(chatIdToSend, [newMessage, assistantMessage], response.updatedContext || {});

          // Actualizar título
          const chatTitle = response.title || 'New chat';
          if (response.title) {
            setChatTitle(chatTitle);
            setChats((prevChats) =>
              prevChats.map((c) =>
                c.id === chatIdToSend
                  ? {
                      ...c,
                      title: chatTitle,
                      name: chatTitle
                    }
                  : c
              )
            );
            updateChatsOrder();
          }

          // CRÍTICO: Actualizar mensajes desde el backend para obtener IDs reales
          // Esto es necesario para que los botones de editar/reenviar funcionen correctamente
          updateChatTimestamp(user.uid, chatIdToSend)
            .then(() => getChatMessages(user.uid, chatIdToSend))
            .then((freshMessages) => {
              if (freshMessages && freshMessages.length > 0) {
                const activeMessages = freshMessages.filter(msg => msg.isActive !== false);
                // Actualizar con los mensajes del backend que tienen IDs reales
                setMessages((current) => mergeMessagesWithUIIds(current, activeMessages));
                setVisibleMessages((current) => mergeMessagesWithUIIds(current, activeMessages));
              }

              // Actualizar timestamp del chat en la lista
              setChats((prevChats) => {
                const updatedAt = new Date().toISOString();
                const nextChats = prevChats.map((c) =>
                  c.id === chatIdToSend
                    ? { ...c, updatedAt }
                    : c
                );
                return nextChats;
              });
              updateChatsOrder();
            })
            .catch((error) => {
              console.warn('[handleNewChatInput] Error actualizando mensajes:', error);
            });

          // Revisar etiqueta AGT
          if (hasAGTTag(response.reply)) {
            handleOpenAGTMaker(response.reply, () => {});
          }

          return;
        }

        // NOTA: El bloque else fue eliminado para forzar el uso de streaming en todos los casos
        // Todos los navegadores modernos soportan SSE (EventSource)
  
      } catch (error) {
        if (error.response?.status === 429) {
          const data = error.response?.data || {};
          dispatch({
            type: 'SET_RATE_LIMIT',
            payload: {
              message: data.message || 'Has alcanzado el límite de mensajes por hora',
              resetIn: data.resetIn || 3600,
              isPro: data.isPro || false
            }
          });
          
          // Finalizar estado de espera en caso de rate limit
          dispatch({ type: 'END_WAITING' });
          return;
        } else {
          dispatch({
            type: 'SET_ERROR',
            payload: {
              message: 'Error al crear nuevo chat o enviar mensaje. Por favor, intenta de nuevo.',
              retryAction: () => handleNewChatInput(messageData)
            }
          });
        }
      } finally {
        // Finalizar estados de procesamiento en un solo dispatch
        dispatch({ 
          type: 'SET_PROCESSING', 
          payload: { isAudio: false, isImage: false } 
        });
        
        // Asegurarse de que el estado de espera termine si no se manejó antes
        if (isWaitingForServerResponse) {
          dispatch({ type: 'RESPONSE_RECEIVED' });
          dispatch({ type: 'END_WAITING' });
        }
      }
    },
    [
      user,
      isWaitingForResponse,
      isWaitingForServerResponse,
      projectContext,
      selectedCategory,
      projectIdForBackend,
      scrollToBottom,
      selectedModel,
      selectedPersonality,
      userName,
      updateChatsOrder,
      handleOpenAGTMaker,
      hasAGTTag,
      saveCurrentChatId,
      setCachedChat,
      setChatContext,
      setCurrentChatId,
      setChats,
      setHasNextPage,
      setLastEvaluatedKey,
      setChatTitle,
      setIsNewChatView,
      processAudioMessage,
      setMessages,
      setVisibleMessages
    ]
  );
  
  // Ahora definimos handleSendMessage después de handleNewChatInput
  const handleSendMessage = useCallback(
    async (messageData) => {
      if (!user || !user.uid || isWaitingForResponse) return;
  
      // Guardamos el chatId en una variable local para no perderlo
      const chatIdToSend = currentChatId;
  
      const isValidMessage =
        typeof messageData.text === 'object' &&
        messageData.text.type === 'audio_message'
          ? true
          : typeof messageData.text === 'string' &&
            messageData.text.trim().length > 0;
  
      if (!isValidMessage && !messageData.files?.length) return;
  
      // Actualizar estados de espera en un solo dispatch
      dispatch({ 
        type: 'START_WAITING', 
        payload: { lastMessageId: null } 
      });

      // Detectar todos los tipos de archivos (imagen/pdf/etc.)
      const isAudioMessage =
        typeof messageData.text === 'object' &&
        messageData.text.type === 'audio_message';
      const hasImageFile = messageData.files?.some((file) =>
        file.type.startsWith('image/')
      );
      const hasPdf = messageData.files?.some((file) => 
        file.type === 'application/pdf'
      );
      const hasExcel = messageData.files?.some((file) =>
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel'
      );
  
      // Actualizar estados de procesamiento en un solo dispatch
      dispatch({ 
        type: 'SET_PROCESSING', 
        payload: { isAudio: isAudioMessage, isImage: hasImageFile } 
      });
      
      // Actualizar info de tipo de mensaje en un solo dispatch
      dispatch({
        type: 'SET_MESSAGE_TYPE_INFO',
        payload: {
          isAudio: isAudioMessage,
          isImage: hasImageFile,
          isPdf: hasPdf,
          isExcel: hasExcel,
          isProjectChat: !!projectContext,
          modelType: selectedModel || 'agt-nest-20250314',
          userIsPro: user?.isPro,
          // Deprecated: Only AGT Nest tier available now
          modelTier: 'agt'
        }
      });
  
      try {
        // Si no existe chatId local, creamos uno nuevo con handleNewChatInput
        if (!chatIdToSend) {
          return await handleNewChatInput(messageData);
        }
        // Procesar mensaje de audio si es necesario
        let processedMessage = null;
        if (isAudioMessage) {
          try {
            processedMessage = await processAudioMessage(messageData);
          } catch (error) {
            // Error handling is silent
          }
        }
  
        // 1) Agregamos el mensaje del user a la UI local
        const userUiId = uuidv4();
        const newMessage = {
          id: userUiId,
          uiId: userUiId,
          text: processedMessage 
            ? JSON.stringify({
                ...processedMessage
              }) 
            : typeof messageData.text === 'object'
              ? JSON.stringify({
                  ...messageData.text,
                  source: messageData.source,
                  captureType: messageData.captureType,
                })
              : messageData.text,
          isUser: true,
          timestamp: Date.now(),
          // MEJORADO: Incluir TODOS los campos relevantes a nivel raíz
          audioS3Url: processedMessage?.audioS3Url,
          audioUrl: processedMessage?.audioS3Url, // También como audioUrl para compatibilidad
          s3Key: processedMessage?.s3Key,  // NUEVO: Incluir s3Key si está disponible
          bucket: processedMessage?.bucket, // NUEVO: Incluir bucket si está disponible
          duration: processedMessage?.duration,
          transcription: processedMessage?.transcription,
          files: messageData.files?.map((file) => ({
            ...file,
            source: messageData.source || file.source,
          })),
          voicePreference: messageData.voicePreference,
          imageBase64Data: messageData.files,
          source: messageData.source,
          captureType: messageData.captureType
        };
  
        setMessages((prev) => [...prev, newMessage]);
        setVisibleMessages((prev) => [...prev, newMessage]);
  
        // Hacemos un scroll al final
        requestAnimationFrame(() => {
          scrollToBottom({ force: true });
        });
        
        // Preparar mensaje de respuesta vacío
        const responseUiId = uuidv4();
        const responseMessage = {
          id: responseUiId,
          uiId: responseUiId,
          text: '',
          isUser: false,
          timestamp: Date.now(),
          imageBase64Data: null,
          source: messageData.source,
          captureType: messageData.captureType
        };
        
        // Actualizar lastMessageId en un solo dispatch
        dispatch({
          type: 'START_WAITING',
          payload: { lastMessageId: responseMessage.id }
        });

        // STREAMING HABILITADO PARA TODOS LOS MODELOS
        if (supportsSSE()) {

          // Inicializar array de pensamientos (vacío para modelos que no son Ultra)
          dispatch({ type: 'SET_ULTRA_THOUGHTS', payload: [] });

          // ⚡ CRÍTICO: Agregar el mensaje vacío del asistente al estado ANTES de comenzar el streaming
          // Esto permite que el callback de tokens encuentre el mensaje para actualizarlo
          setMessages((prevMessages) => [...prevMessages, responseMessage]);
          setVisibleMessages((prevMessages) => [...prevMessages, responseMessage]);

          // Preparar el mensaje para enviar
          const finalMessage = processedMessage || {
            text: messageData.text,
            files: messageData.files,
            voicePreference: messageData.voicePreference,
            source: messageData.source,
            captureType: messageData.captureType,
            projectId: projectIdForBackend,
            metadata: {
              source: messageData.source,
              captureType: messageData.captureType,
              deviceInfo: 'web',
              timestamp: new Date().toISOString()
            },
          };
          
          // Enviar mensaje con streaming
          let streamingText = '';
          let wasNearBottom = true; // Tracking si el usuario estaba cerca del fondo

          // Función helper para verificar si el usuario está cerca del fondo
          // (igual que ChatGPT/Claude.ai)
          const isUserNearBottom = () => {
            const container = messagesEndRef?.current?.parentElement;
            if (!container) return true;

            const threshold = 150; // px desde el fondo (igual que ChatGPT)
            const position = container.scrollHeight - container.scrollTop - container.clientHeight;
            return position < threshold;
          };

          // Verificar posición inicial
          wasNearBottom = isUserNearBottom();

          // Crear AbortController para poder cancelar el streaming
          abortControllerRef.current = new AbortController();

          // Marcar que el streaming ha comenzado (antes de la llamada)
          isStreamingRef.current = true; // Actualizar ref inmediatamente
          dispatch({ type: 'START_STREAMING' });

          const response = await sendMessageToAGTStream(
            finalMessage,
            user.uid,
            chatIdToSend,
            chatContext,
            selectedModel || 'agt-nest-20250314',
            selectedPersonality || 'normal',
            userName,
            user?.email || '',
            messageData.voicePreference,
            // onToken callback - se llama cada vez que llega un token
            (token) => {
              // Si es el streamId, guardarlo y retornar
              if (typeof token === 'object' && token.type === 'stream_id') {
                currentStreamIdRef.current = token.streamId;
                return;
              }

              // Manejar eventos de tools
              if (typeof token === 'object') {
                console.log('🔍 [Frontend] Token recibido:', token);
                switch (token.type) {
                  case 'tool_marker':
                    console.log('📍📍📍 [Frontend] MARCADOR RECIBIDO:', token.marker, 'para', token.toolName);
                    console.log('📍 [Frontend] streamingText antes:', streamingText.substring(0, 100));

                    // VALIDACIÓN: Asegurar que token.marker es un string
                    if (typeof token.marker !== 'string') {
                      console.error('⚠️ [Frontend] token.marker NO es string:', {
                        type: typeof token.marker,
                        value: token.marker,
                        fullToken: token
                      });
                      return;
                    }

                    ensureToolTracked(token.toolId, token.toolName, 'initializing', streamingText.length);

                    // Inyectar el marcador en el texto del stream
                    streamingText += `\n${token.marker}\n`;
                    console.log('📍 [Frontend] streamingText después:', streamingText.substring(0, 200));

                    // Actualizar el mensaje en tiempo real con el marcador
                    setMessages((prev) => {
                      const newMessages = [...prev];
                      const lastIndex = newMessages.length - 1;
                      if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                        newMessages[lastIndex] = {
                          ...newMessages[lastIndex],
                          text: streamingText
                        };
                      }
                      return newMessages;
                    });

                    setVisibleMessages((prev) => {
                      const newMessages = [...prev];
                      const lastIndex = newMessages.length - 1;
                      if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                        newMessages[lastIndex] = {
                          ...newMessages[lastIndex],
                          text: streamingText
                        };
                      }
                      return newMessages;
                    });
                    return;

                  case 'tool_start':
                    console.log('🔧 [Frontend] Tool start:', token.toolName, `(posición: ${streamingText.length})`);
                    dispatch({
                      type: 'ADD_TOOL',
                      payload: {
                        toolId: token.toolId,
                        toolName: token.toolName,
                        status: 'initializing', // Estado inicial: preparando
                        input: token.input,
                        textPositionStart: streamingText.length, // Guardar posición cuando inicia
                        marker: `__TOOL_MARKER_${token.toolId}__` // Guardar el marcador
                      }
                    });
                    return;

                  case 'tool_executing':
                    console.log('⚙️ [Frontend] Tool executing:', token.toolName);
                    ensureToolTracked(token.toolId, token.toolName, 'running', streamingText.length);
                    dispatch({
                      type: 'UPDATE_TOOL_STATUS',
                      payload: {
                        toolId: token.toolId,
                        status: 'running' // Ahora está ejecutándose
                      }
                    });
                    return;

                  case 'tool_progress':
                    console.log('📍 [Frontend] Tool progress:', token.toolName, '-', token.phase);
                    ensureToolTracked(token.toolId, token.toolName, 'running', streamingText.length);
                    dispatch({
                      type: 'ADD_TOOL_PROGRESS',
                      payload: {
                        toolId: token.toolId,
                        phase: token.phase,
                        elapsed: token.elapsed
                      }
                    });
                    return;

                  case 'tool_complete':
                    console.log('✅ [Frontend] Tool complete:', token.toolName, `(${token.executionTime}ms, posición: ${streamingText.length})`);
                    ensureToolTracked(token.toolId, token.toolName, 'completed', streamingText.length);
                    dispatch({
                      type: 'UPDATE_TOOL_STATUS',
                      payload: {
                        toolId: token.toolId,
                        status: 'completed',
                        executionTime: token.executionTime,
                        input: token.input,
                        phases: token.phases,
                        textPositionEnd: streamingText.length // Guardar posición cuando termina
                      }
                    });
                    trackChatToolUsage(chatIdToSend || currentChatId, token.toolName, token.input);
                    syncSelectedAppsFromTool(token.toolName, token.input);
                    navigateFromTool(token.toolName, token.input, chatIdToSend || currentChatId);
                    return;

                  case 'tool_error':
                    console.error('❌ [Frontend] Tool error:', token.toolName, token.error);
                    ensureToolTracked(token.toolId, token.toolName, 'error', streamingText.length);
                    dispatch({
                      type: 'UPDATE_TOOL_STATUS',
                      payload: {
                        toolId: token.toolId,
                        status: 'error',
                        error: token.error,
                        textPositionEnd: streamingText.length // Guardar posición cuando falla
                      }
                    });
                    return;

                  case 'executing_tools':
                    console.log(`⚙️ [Frontend] Ejecutando ${token.count} tool(s)`);
                    return;

                  case 'continuing_stream':
                    console.log('🔄 [Frontend] Continuando stream con resultados');
                    return;

                  default:
                    // Si es un objeto pero no es un tipo conocido, no agregarlo al texto
                    console.warn('⚠️ [Frontend] Token objeto desconocido:', token);
                    return;
                }
              }

              // Solo agregar al texto si es un string
              if (typeof token === 'string') {
                streamingText += token;
              } else {
                console.warn('⚠️ [Frontend] Token no es string ni objeto conocido:', token);
                return;
              }

              // Actualizar el mensaje del asistente en tiempo real
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                  newMessages[lastIndex] = {
                    ...newMessages[lastIndex],
                    text: streamingText
                  };
                }
                return newMessages;
              });

              setVisibleMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                  newMessages[lastIndex] = {
                    ...newMessages[lastIndex],
                    text: streamingText
                  };
                }
                return newMessages;
              });

              // Auto-scroll SOLO si el usuario estaba cerca del fondo
              // (comportamiento idéntico a ChatGPT/Claude.ai)
              if (wasNearBottom) {
                // Esperar a que React actualice el DOM
                requestAnimationFrame(() => {
                  scrollToBottom({ force: false });

                  // Actualizar el estado después del scroll
                  // Si el usuario scrollea manualmente hacia arriba, se detectará en el siguiente token
                  setTimeout(() => {
                    wasNearBottom = isUserNearBottom();
                  }, 50);
                });
              } else {
                // Verificar periódicamente si el usuario volvió al fondo
                setTimeout(() => {
                  wasNearBottom = isUserNearBottom();
                }, 50);
              }
            },
            // onDone callback - se llama cuando termina el streaming
            (result) => {
              console.log('🏁 [Frontend] onDone - iniciando finalización del stream');

              // Marcar streaming finalizado antes de cualquier dispatch/render
              isStreamingRef.current = false;

              // CRÍTICO: Guardar snapshot de tools INMEDIATAMENTE antes de cualquier otro update
              const toolsSnapshot = [...activeToolsRef.current];
              console.log('💾 [Frontend] Guardando tools en mensaje:', JSON.stringify(toolsSnapshot, null, 2));
              console.log('💾 [Frontend] Número de tools:', toolsSnapshot.length);

              // Actualizar el mensaje del asistente con las tools usadas INMEDIATAMENTE
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                  const updatedMessage = {
                    ...newMessages[lastIndex],
                    toolsUsed: toolsSnapshot.length > 0 ? toolsSnapshot : undefined
                  };
                  console.log('📝 [Frontend] Mensaje actualizado con tools:', {
                    messageId: updatedMessage.id,
                    toolsUsed: updatedMessage.toolsUsed,
                    toolsCount: toolsSnapshot.length
                  });
                  newMessages[lastIndex] = updatedMessage;
                }
                return newMessages;
              });

              setVisibleMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                  const updatedMessage = {
                    ...newMessages[lastIndex],
                    toolsUsed: toolsSnapshot.length > 0 ? toolsSnapshot : undefined
                  };
                  newMessages[lastIndex] = updatedMessage;
                }
                return newMessages;
              });

              // Limpiar activeTools DESPUÉS de guardar en el mensaje
              dispatch({ type: 'CLEAR_TOOLS' });
              console.log('🧹 [Frontend] activeTools limpiados después de guardar');

              // Actualizar contexto
              if (result.updatedContext) {
                setChatContext(result.updatedContext);
              }

              // Scroll final forzado (asegurar que se vea el mensaje completo)
              requestAnimationFrame(() => {
                scrollToBottom({ force: true });
              });

              // IMPORTANTE: Indicar que se recibió respuesta del servidor
              dispatch({ type: 'RESPONSE_RECEIVED' });
              dispatch({ type: 'END_WAITING' });
            },
            // onError callback
            (error) => {
              console.error('[STREAMING] ❌ Error:', error);

              // Si el error es por cancelación (abort), guardar el mensaje parcial
              if (error.name === 'AbortError') {

                // Actualizar el mensaje con el texto parcial que teníamos
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastIndex = newMessages.length - 1;
                  if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                    newMessages[lastIndex] = {
                      ...newMessages[lastIndex],
                      text: streamingText,
                      wasCancelled: true, // Marcar que fue cancelado
                    };
                  }
                  return newMessages;
                });

                setVisibleMessages((prev) => {
                  const newMessages = [...prev];
                  const lastIndex = newMessages.length - 1;
                  if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                    newMessages[lastIndex] = {
                      ...newMessages[lastIndex],
                      text: streamingText,
                      wasCancelled: true,
                    };
                  }
                  return newMessages;
                });

                dispatch({ type: 'END_WAITING' });

                // CRÍTICO: Refrescar mensajes desde el backend para obtener IDs correctos
                // Esto es necesario para que los botones de editar/reenviar funcionen después de cancelar
                updateChatTimestamp(user.uid, chatIdToSend)
                  .then(() => getChatMessages(user.uid, chatIdToSend))
                  .then((freshMessages) => {
                    if (freshMessages && freshMessages.length > 0) {
                      const activeMessages = freshMessages.filter(msg => msg.isActive !== false);
                      setMessages(activeMessages);
                      setVisibleMessages(activeMessages);
                    }
                  })
                  .catch((refreshError) => {
                    console.error('[STREAMING] ⚠️ Error refrescando mensajes:', refreshError);
                    // No fallar si no se pueden refrescar los mensajes
                  });

                return;
              }

              dispatch({ type: 'END_WAITING' });
              dispatch({
                type: 'SET_ERROR',
                payload: {
                  message: error.message || 'Error al procesar el mensaje',
                  retryAction: null
                }
              });
            },
            abortControllerRef.current?.signal,
            // onPreferencesUpdated callback
            (updatedPreferences) => {
              console.log('🔔 [HOOK] Preferencias actualizadas por el asistente:', updatedPreferences);
              if (onUserPreferencesChanged) {
                onUserPreferencesChanged(updatedPreferences);
              }
            }
          );

          // Capturar y registrar URL de audio si existe
          const audioUrl = response.audioUrl || response.audioS3Url;
          const s3Key = response.s3Key;
          const bucket = response.bucket;

          // Actualizar respuesta
          dispatch({
            type: 'SET_LAST_ASSISTANT_MESSAGE',
            payload: {
              text: response.reply || streamingText,
              audioBlob: response.audioBlob,
              audioUrl,
              audioS3Url: audioUrl,
              s3Key,
              bucket,
              duration: response.duration || 0,
              isUser: false,
            }
          });

          // IMPORTANTE: NO reemplazar el mensaje completo, solo actualizar campos necesarios
          // para evitar re-render que causa pestañeo
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastIndex = newMessages.length - 1;
            if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
              const previousMessage = newMessages[lastIndex];

              // CRÍTICO: Si el mensaje anterior tiene marcadores, NO reemplazar el texto
              // porque response.reply del backend NO contiene los marcadores
              const hasMarkers = previousMessage.text?.includes('__TOOL_MARKER_');
              const shouldKeepPreviousText = hasMarkers && response.reply && !response.reply.includes('__TOOL_MARKER_');

              // SOLO actualizar campos que podrían haber cambiado, preservando el resto
              newMessages[lastIndex] = {
                ...previousMessage, // ← Preservar TODO del mensaje anterior
                text: shouldKeepPreviousText ? previousMessage.text : (response.reply || previousMessage.text),
                audioUrl: audioUrl || previousMessage.audioUrl,
                audioS3Url: audioUrl || previousMessage.audioS3Url,
                s3Key: s3Key || previousMessage.s3Key,
                bucket: bucket || previousMessage.bucket,
                duration: response.duration || previousMessage.duration || 0,
                // toolsUsed ya está en previousMessage, no hace falta preservar
              };
              console.log('🔧 [Frontend] Actualizando mensaje sin reemplazar (evitar pestañeo):', {
                messageId: newMessages[lastIndex].id,
                toolsUsed: newMessages[lastIndex].toolsUsed,
                hasMarkers: newMessages[lastIndex].text?.includes('__TOOL_MARKER_'),
                preservedText: shouldKeepPreviousText
              });
            }
            return newMessages;
          });
          setVisibleMessages((prev) => {
            const newMessages = [...prev];
            const lastIndex = newMessages.length - 1;
            if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
              const previousMessage = newMessages[lastIndex];

              // CRÍTICO: Si el mensaje anterior tiene marcadores, NO reemplazar el texto
              const hasMarkers = previousMessage.text?.includes('__TOOL_MARKER_');
              const shouldKeepPreviousText = hasMarkers && response.reply && !response.reply.includes('__TOOL_MARKER_');

              // SOLO actualizar campos que podrían haber cambiado, preservando el resto
              newMessages[lastIndex] = {
                ...previousMessage, // ← Preservar TODO del mensaje anterior
                text: shouldKeepPreviousText ? previousMessage.text : (response.reply || previousMessage.text),
                audioUrl: audioUrl || previousMessage.audioUrl,
                audioS3Url: audioUrl || previousMessage.audioS3Url,
                s3Key: s3Key || previousMessage.s3Key,
                bucket: bucket || previousMessage.bucket,
                duration: response.duration || previousMessage.duration || 0,
              };
            }
            return newMessages;
          });

          // Crear assistantMessage SOLO para el caché, usando los datos actualizados
          const assistantMessage = {
            ...responseMessage,
            uiId: responseMessage.uiId || responseMessage.id,
            text: response.reply || streamingText,
            audioUrl,
            audioS3Url: audioUrl,
            s3Key,
            bucket,
            duration: response.duration || 0,
            // Incluir toolsUsed en el caché si está disponible
            toolsUsed: undefined // El caché no necesita toolsUsed
          };

          // Actualizar contexto
          const updatedContext = response.updatedContext || {};
          setChatContext(updatedContext);
          setCachedChat(chatIdToSend, [
            ...(messages || []),
            newMessage,
            assistantMessage
          ], updatedContext);

          // CRÍTICO: Actualizar mensajes desde el backend para obtener IDs reales
          // Esto es necesario para que los botones de editar/reenviar funcionen correctamente
          updateChatTimestamp(user.uid, chatIdToSend)
            .then(() => getChatMessages(user.uid, chatIdToSend))
            .then((freshMessages) => {
              if (freshMessages && freshMessages.length > 0) {
                const activeMessages = freshMessages.filter(msg => msg.isActive !== false);
                // Actualizar con los mensajes del backend que tienen IDs reales
                setMessages((current) => mergeMessagesWithUIIds(current, activeMessages));
                setVisibleMessages((current) => mergeMessagesWithUIIds(current, activeMessages));
              }

              // Actualizar timestamp y título del chat en la lista
              setChats((prevChats) => {
                const updatedAt = new Date().toISOString();
                const nextChats = prevChats.map((c) =>
                  c.id === chatIdToSend
                    ? {
                        ...c,
                        updatedAt,
                        title: response.title || c.title,
                        name: response.title || c.name
                      }
                    : c
                );
                return nextChats;
              });
              updateChatsOrder();
            })
            .catch((error) => {
              console.warn('[handleSendMessage] Error actualizando mensajes:', error);
            });

          // Actualizar título si es necesario
          if (response.title) {
            setChatTitle(response.title);
            setChats((prevChats) =>
              prevChats.map((c) =>
                c.id === chatIdToSend
                  ? {
                      ...c,
                      title: response.title,
                      name: response.title,
                      updatedAt: new Date().toISOString()
                    }
                  : c
              )
            );
            updateChatsOrder();
          }

          // Revisar etiqueta AGT
          if (hasAGTTag(response.reply)) {
            handleOpenAGTMaker(response.reply, () => {});
          }

          return;
        }

        // NOTA: El bloque else fue eliminado para forzar el uso de streaming en todos los casos
        // Todos los navegadores modernos soportan SSE (EventSource)
  
      } catch (error) {
        if (error.response?.status === 429) {
          // De todos modos, si el request se hizo directo => fallback
          const data = error.response?.data || {};
          dispatch({
            type: 'SET_RATE_LIMIT',
            payload: {
              message: data.message || 'Has alcanzado el límite de mensajes por hora',
              resetIn: data.resetIn || 3600,
              isPro: data.isPro || false
            }
          });
          
          // Finalizar estado de espera en caso de rate limit
          dispatch({ type: 'END_WAITING' });
          return;
        } else {
          dispatch({
            type: 'SET_ERROR',
            payload: {
              message: 'Error al enviar mensaje. Por favor, intenta de nuevo.',
              retryAction: () => handleSendMessage(messageData)
            }
          });
        }
      } finally {
        // Finalizar estados de procesamiento en un solo dispatch
        dispatch({ 
          type: 'SET_PROCESSING', 
          payload: { isAudio: false, isImage: false } 
        });
        
        // Asegurarse de que el estado de espera de servidor termine si no se manejó antes
        if (isWaitingForServerResponse) {
          dispatch({ type: 'RESPONSE_RECEIVED' });
          dispatch({ type: 'END_WAITING' });
        }
      }
    },
    [
      user,
      currentChatId,
      isWaitingForResponse,
      isWaitingForServerResponse,
      chatContext,
      selectedModel,
      selectedPersonality,
      userName,
      projectContext,
      updateChatsOrder,
      hasAGTTag,
      scrollToBottom,
      handleNewChatInput,
      projectIdForBackend,
      handleOpenAGTMaker,
      messages,
      setCachedChat,
      setChatContext,
      setChats,
      setHasNextPage,
      setLastEvaluatedKey,
      setChatTitle,
      processAudioMessage,
      setMessages,
      setVisibleMessages
    ]
  );

  // Función para manejar el retry de acciones tras error
  const handleRetry = useCallback(() => {
    if (retryAction) {
      retryAction();
      dispatch({ type: 'CLEAR_ERROR' });
    }
  }, [retryAction]);

  // Setter para error con retryAction
  const setError = useCallback((message, retryFn = null) => {
    dispatch({
      type: 'SET_ERROR',
      payload: {
        message,
        retryAction: retryFn
      }
    });
  }, []);

  // Setter para retryAction
  const setRetryAction = useCallback((retryFn) => {
    if (!retryFn) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          message: error,
          retryAction: null
        }
      });
    } else {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          message: error,
          retryAction: retryFn
        }
      });
    }
  }, [error]);

  // Función para detener el streaming
  const stopStreaming = useCallback(async () => {
    if (abortControllerRef.current) {

      // Primero, notificar al backend para que detenga el stream
      if (currentStreamIdRef.current) {
        await cancelAGTStream(currentStreamIdRef.current);
        currentStreamIdRef.current = null;
      }

      // Luego, cancelar la solicitud en el frontend
      abortControllerRef.current.abort();
      abortControllerRef.current = null;

      // IMPORTANTE: Actualizar ref + dispatch usando helper (evita race conditions)
      endStreamingState();

    }
  }, [endStreamingState]);

  /* =========================================================================
     HANDLERS DE EDICIÓN Y VERSIONADO DE MENSAJES
     ========================================================================= */

  /**
   * Edita un mensaje de usuario y crea una nueva versión
   */
  const handleEditMessage = useCallback(async (messageId, newText) => {
    if (!user || !currentChatId) {
      console.error('[EDIT] ❌ Missing user or chatId');
      return;
    }

    // IMPORTANTE: Usar isStreamingRef.current en lugar de isStreaming para evitar race conditions
    // El ref se actualiza inmediatamente en stopStreaming(), mientras que el estado es asíncrono
    const currentlyStreaming = isStreamingRef.current;

    if (currentlyStreaming) {
      // Solo bloquear si intentas editar el último mensaje del usuario mientras genera respuesta
      const lastUserMessage = messages.slice().reverse().find(msg => msg.isUser);
      if (lastUserMessage && lastUserMessage.id === messageId) {
        console.warn('[EDIT] ⚠️ No se puede editar el último mensaje durante streaming');
        return;
      }
      // Permitir editar mensajes anteriores incluso durante streaming
    }

    // Encontrar el mensaje para debugging
    const message = messages.find(msg => msg.id === messageId);
    console.log('[EDIT] 📝 Editando mensaje:', {
      messageId,
      message: message ? {
        id: message.id,
        uiId: message.uiId,
        text: message.text?.substring(0, 50),
        isUser: message.isUser
      } : 'NO ENCONTRADO'
    });

    try {
      const result = await editMessage(user.uid, currentChatId, messageId, newText);


      // Refrescar mensajes desde el backend para asegurar consistencia
      const freshMessages = await getChatMessages(user.uid, currentChatId);

      // Filtrar solo mensajes activos
      const activeOnly = freshMessages.filter(msg => msg.isActive !== false);

      // Actualizar ambos estados preservando uiId para evitar remounts
      setMessages((current) => mergeMessagesWithUIIds(current, activeOnly));
      setVisibleMessages((current) => mergeMessagesWithUIIds(current, activeOnly));


      // Si el backend indica que debe generar una nueva respuesta, hacerlo automáticamente
      if (result.shouldGenerateResponse) {

        // IMPORTANTE: Limpiar activeTools de la respuesta anterior antes de generar nueva respuesta
        // Esto evita que los ToolIndicators fantasma se muestren hasta que la nueva respuesta termine
        dispatch({ type: 'UPDATE_ACTIVE_TOOLS', payload: [] });
        activeToolsRef.current = [];
        console.log('🧹 [Edit] Limpiando activeTools antes de generar nueva respuesta');

        // Scroll al final para mostrar el nuevo mensaje
        requestAnimationFrame(() => {
          scrollToBottom({ force: true });
        });

        // Preparar mensaje placeholder para la respuesta del asistente
        const responseUiId = uuidv4();
        const responseMessage = {
          id: responseUiId,
          uiId: responseUiId,
          text: '',
          isUser: false,
          timestamp: Date.now(),
          isLoading: false
        };

        // Actualizar lastMessageId en un solo dispatch
        dispatch({
          type: 'START_WAITING',
          payload: { lastMessageId: responseMessage.id }
        });

        // STREAMING - Agregar mensaje placeholder ANTES de iniciar
        flushSync(() => {
          setMessages((prev) => [...prev, responseMessage]);
          setVisibleMessages((prev) => [...prev, responseMessage]);
        });

        // Marcar que el streaming ha comenzado
        isStreamingRef.current = true; // Actualizar ref inmediatamente
        dispatch({ type: 'START_STREAMING' });

        // Preparar el mensaje para enviar (solo texto, el usuario ya está guardado)
        const finalMessage = {
          text: newText,
          voicePreference: 'none',
          source: 'web',
          captureType: 'text',
          // IMPORTANTE: Indicar que este mensaje es una edición
          // para que el backend use el messageId correcto como parentVersionId
          editedMessageId: result.newVersion?.messageId, // ID de la versión editada
          isEditedMessage: true, // Flag para indicar que es una edición
          metadata: {
            source: 'web',
            captureType: 'text',
            deviceInfo: 'web',
            timestamp: new Date().toISOString(),
            isEditedMessage: true,
            editedMessageId: result.newVersion?.messageId
          },
        };

        let streamingText = '';
        let wasNearBottom = true;

        // Función helper para verificar si el usuario está cerca del fondo
        const isUserNearBottom = () => {
          const container = messagesEndRef?.current?.parentElement;
          if (!container) return true;
          const threshold = 150;
          const position = container.scrollHeight - container.scrollTop - container.clientHeight;
          return position < threshold;
        };

        wasNearBottom = isUserNearBottom();

        // Crear AbortController para poder cancelar el streaming
        abortControllerRef.current = new AbortController();

        // Enviar mensaje con streaming
        const response = await sendMessageToAGTStream(
          finalMessage,
          user.uid,
          currentChatId,
          chatContext,
          selectedModel || 'agt-nest-20250314',
          selectedPersonality || 'normal',
          userName,
          user?.email || '',
          'none', // voicePreference
          // onToken callback
          (token) => {
            // Si es el streamId, guardarlo y retornar
            if (typeof token === 'object' && token.type === 'stream_id') {
              currentStreamIdRef.current = token.streamId;
              return;
            }

            // Manejar eventos de tools (igual que en handleNewChatInput)
            if (typeof token === 'object') {
              console.log('🔍 [Frontend-Edit] Token recibido:', token);
              switch (token.type) {
                case 'tool_marker':
                  console.log('📍📍📍 [Frontend-Edit] MARCADOR RECIBIDO:', token.marker, 'para', token.toolName);
                  console.log('📍 [Frontend-Edit] streamingText antes:', streamingText.substring(0, 100));

                  // VALIDACIÓN: Asegurar que token.marker es un string
                  if (typeof token.marker !== 'string') {
                    console.error('⚠️ [Frontend-Edit] token.marker NO es string:', {
                      type: typeof token.marker,
                      value: token.marker,
                      fullToken: token
                    });
                    return;
                  }

                  ensureToolTracked(token.toolId, token.toolName, 'initializing', streamingText.length);

                  // Inyectar el marcador en el texto del stream
                  streamingText += `\n${token.marker}\n`;
                  console.log('📍 [Frontend-Edit] streamingText después:', streamingText.substring(0, 200));

                  // Actualizar el mensaje en tiempo real con el marcador
                  setMessages((prev) => {
                    const newMessages = [...prev];
                    const lastIndex = newMessages.length - 1;
                    if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                      newMessages[lastIndex] = {
                        ...newMessages[lastIndex],
                        text: streamingText
                      };
                    }
                    return newMessages;
                  });

                  setVisibleMessages((prev) => {
                    const newMessages = [...prev];
                    const lastIndex = newMessages.length - 1;
                    if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                      newMessages[lastIndex] = {
                        ...newMessages[lastIndex],
                        text: streamingText
                      };
                    }
                    return newMessages;
                  });
                  return;

                case 'tool_start':
                  console.log('🔧 [Frontend-Edit] Tool start:', token.toolName, `(posición: ${streamingText.length})`);
                  dispatch({
                    type: 'ADD_TOOL',
                    payload: {
                      toolId: token.toolId,
                      toolName: token.toolName,
                      status: 'initializing', // Estado inicial: preparando
                      input: token.input,
                      textPositionStart: streamingText.length, // Guardar posición cuando inicia
                      marker: `__TOOL_MARKER_${token.toolId}__` // Guardar el marcador
                    }
                  });
                  return;

                case 'tool_executing':
                  console.log('⚙️ [Frontend-Edit] Tool executing:', token.toolName);
                  ensureToolTracked(token.toolId, token.toolName, 'running', streamingText.length);
                  dispatch({
                    type: 'UPDATE_TOOL_STATUS',
                    payload: {
                      toolId: token.toolId,
                      status: 'running' // Ahora está ejecutándose
                    }
                  });
                  return;

                case 'tool_progress':
                  console.log('📍 [Frontend-Edit] Tool progress:', token.toolName, '-', token.phase);
                  ensureToolTracked(token.toolId, token.toolName, 'running', streamingText.length);
                  dispatch({
                    type: 'ADD_TOOL_PROGRESS',
                    payload: {
                      toolId: token.toolId,
                      phase: token.phase,
                      elapsed: token.elapsed
                    }
                  });
                  return;

                case 'tool_complete':
                  console.log('✅ [Frontend-Edit] Tool complete:', token.toolName, `(${token.executionTime}ms, posición: ${streamingText.length})`);
                  ensureToolTracked(token.toolId, token.toolName, 'completed', streamingText.length);
                  dispatch({
                    type: 'UPDATE_TOOL_STATUS',
                    payload: {
                      toolId: token.toolId,
                      status: 'completed',
                      executionTime: token.executionTime,
                      input: token.input,
                      phases: token.phases,
                      textPositionEnd: streamingText.length // Guardar posición cuando termina
                    }
                  });
                  trackChatToolUsage(currentChatId, token.toolName, token.input);
                  navigateFromTool(token.toolName, token.input, currentChatId);
                  return;

                case 'tool_error':
                  console.error('❌ [Frontend-Edit] Tool error:', token.toolName, token.error);
                  ensureToolTracked(token.toolId, token.toolName, 'error', streamingText.length);
                  dispatch({
                    type: 'UPDATE_TOOL_STATUS',
                    payload: {
                      toolId: token.toolId,
                      status: 'error',
                      error: token.error,
                      textPositionEnd: streamingText.length // Guardar posición cuando falla
                    }
                  });
                  return;

                case 'executing_tools':
                  console.log(`⚙️ [Frontend-Edit] Ejecutando ${token.count} tool(s)`);
                  return;

                case 'continuing_stream':
                  console.log('🔄 [Frontend-Edit] Continuando stream con resultados');
                  return;

                default:
                  // Si es un objeto pero no es un tipo conocido, no agregarlo al texto
                  console.warn('⚠️ [Frontend-Edit] Token objeto desconocido:', token);
                  return;
              }
            }

            // Solo agregar al texto si es un string
            if (typeof token === 'string') {
              streamingText += token;
            } else {
              console.warn('⚠️ [Frontend-Edit] Token no es string:', typeof token);
              return;
            }

            // Actualizar el mensaje del asistente en tiempo real
            setMessages((prev) => {
              const newMessages = [...prev];
              const lastIndex = newMessages.length - 1;
              if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                newMessages[lastIndex] = {
                  ...newMessages[lastIndex],
                  text: streamingText
                };
              }
              return newMessages;
            });

            setVisibleMessages((prev) => {
              const newMessages = [...prev];
              const lastIndex = newMessages.length - 1;
              if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                newMessages[lastIndex] = {
                  ...newMessages[lastIndex],
                  text: streamingText
                };
              }
              return newMessages;
            });

            // Auto-scroll si el usuario estaba cerca del fondo
            if (wasNearBottom) {
              requestAnimationFrame(() => {
                scrollToBottom({ force: false });
                setTimeout(() => {
                  wasNearBottom = isUserNearBottom();
                }, 50);
              });
            } else {
              setTimeout(() => {
                wasNearBottom = isUserNearBottom();
              }, 50);
            }
          },
          // onDone callback
          (result) => {
            console.log('🏁 [Frontend-Edit] onDone - iniciando finalización del stream');

            // Marcar streaming finalizado antes de dispatch/render
            isStreamingRef.current = false;

            // CRÍTICO: Guardar snapshot de tools INMEDIATAMENTE antes de cualquier otro update
            const toolsSnapshot = [...activeToolsRef.current];
            console.log('💾 [Frontend-Edit] Guardando tools en mensaje:', JSON.stringify(toolsSnapshot, null, 2));
            console.log('💾 [Frontend-Edit] Número de tools:', toolsSnapshot.length);

            // Actualizar el mensaje del asistente con las tools usadas INMEDIATAMENTE
            setMessages((prev) => {
              const newMessages = [...prev];
              const lastIndex = newMessages.length - 1;
              if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                const updatedMessage = {
                  ...newMessages[lastIndex],
                  toolsUsed: toolsSnapshot.length > 0 ? toolsSnapshot : undefined
                };
                console.log('📝 [Frontend-Edit] Mensaje actualizado con tools:', {
                  messageId: updatedMessage.id,
                  toolsUsed: updatedMessage.toolsUsed,
                  toolsCount: toolsSnapshot.length
                });
                newMessages[lastIndex] = updatedMessage;
              }
              return newMessages;
            });

            setVisibleMessages((prev) => {
              const newMessages = [...prev];
              const lastIndex = newMessages.length - 1;
              if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                const updatedMessage = {
                  ...newMessages[lastIndex],
                  toolsUsed: toolsSnapshot.length > 0 ? toolsSnapshot : undefined
                };
                newMessages[lastIndex] = updatedMessage;
              }
              return newMessages;
            });

            // Limpiar activeTools DESPUÉS de guardar en el mensaje
            dispatch({ type: 'CLEAR_TOOLS' });
            console.log('🧹 [Frontend-Edit] activeTools limpiados después de guardar');

            if (result.updatedContext) {
              setChatContext(result.updatedContext);
            }
            requestAnimationFrame(() => {
              scrollToBottom({ force: true });
            });
            dispatch({ type: 'RESPONSE_RECEIVED' });
            dispatch({ type: 'END_WAITING' });
          },
          // onError callback
          (error) => {
            console.error('[EDIT] ❌ Error en streaming:', error);
            dispatch({ type: 'END_WAITING' });
            dispatch({
              type: 'SET_ERROR',
              payload: {
                message: error.message || 'Error al procesar el mensaje',
                retryAction: null
              }
            });
          },
          abortControllerRef.current?.signal,
          // onPreferencesUpdated callback - TERCERA LLAMADA (handleEditMessage)
          (updatedPreferences) => {
            console.log('🔔 [HOOK-3] Preferencias actualizadas por el asistente:', updatedPreferences);
            if (onUserPreferencesChanged) {
              onUserPreferencesChanged(updatedPreferences);
            }
          }
        );

      }

    } catch (error) {
      console.error('[EDIT] ❌ Error editando mensaje:', error);

      // Extraer mensaje de error específico del backend
      let errorMessage = 'Failed to edit message';
      if (error.message) {
        // Si el error menciona el límite de versiones, usar mensaje más claro
        if (error.message.includes('Maximum 5 versions') || error.message.includes('maximum') || error.message.includes('versions allowed')) {
          errorMessage = 'Maximum of 5 versions reached. You cannot create more versions of this message.';
        } else {
          errorMessage = error.message;
        }
      }

      dispatch({
        type: 'SET_ERROR',
        payload: {
          message: errorMessage,
          retryAction: error.message.includes('Maximum 5 versions') ? null : () => handleEditMessage(messageId, newText)
        }
      });
    }
  }, [
    user,
    currentChatId,
    isWaitingForResponse,
    isStreaming, // IMPORTANTE: Necesario para evitar stale closure
    messages, // IMPORTANTE: Necesario para verificar el último mensaje
    setMessages,
    setVisibleMessages,
    scrollToBottom,
    chatContext,
    selectedModel,
    selectedPersonality,
    userName,
    messagesEndRef,
    abortControllerRef,
    currentStreamIdRef,
    setChatContext
  ]);

  /**
   * Reenvía un mensaje para obtener una respuesta diferente
   * IMPORTANTE: Funciona igual que editar, pero con el mismo texto
   * Esto crea una nueva versión del mensaje y genera una nueva respuesta
   */
  const handleResendMessage = useCallback(async (messageId) => {
    if (!user || !currentChatId) {
      return;
    }

    // IMPORTANTE: Usar isStreamingRef.current en lugar de isStreaming para evitar race conditions
    const currentlyStreaming = isStreamingRef.current;

    if (currentlyStreaming) {
      // Solo bloquear si intentas reenviar el último mensaje del usuario mientras genera respuesta
      const lastUserMessage = messages.slice().reverse().find(msg => msg.isUser);
      if (lastUserMessage && lastUserMessage.id === messageId) {
        return;
      }
      // Permitir reenviar mensajes anteriores incluso durante streaming
    } else {
    }

    try {
      const message = messages.find(msg => msg.id === messageId);
      if (!message) {
        console.error('[RESEND] ❌ Mensaje no encontrado:', messageId);
        return;
      }

      // Extraer el texto del mensaje (puede estar en diferentes formatos)
      let messageText = message.text;

      // Si el texto es un objeto JSON, extraerlo
      if (typeof messageText === 'string') {
        try {
          const parsed = JSON.parse(messageText);
          if (parsed.text) {
            messageText = parsed.text;
          }
        } catch (e) {
          // No es JSON, usar el texto tal cual
        }
      }


      // Llamar a handleEditMessage con el mismo texto para crear una nueva versión
      // Esto funcionará exactamente como editar, pero con el mismo contenido
      await handleEditMessage(messageId, messageText);

    } catch (error) {
      console.error('[RESEND] ❌ Error reenviando mensaje:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: {
          message: 'Failed to resend message',
          retryAction: () => handleResendMessage(messageId)
        }
      });
    }
  }, [user, currentChatId, messages, handleEditMessage]);

  /**
   * Pre-carga versiones adyacentes en segundo plano para navegación instantánea
   */
  const preloadAdjacentVersions = useCallback((baseMessageId, currentIndex, totalVersions) => {
    if (!user || !currentChatId) return;

    // Pre-cargar versión anterior (si existe)
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      const prevCacheKey = `${currentChatId}-${baseMessageId}-${prevIndex}`;

      if (!versionsCacheRef.current.has(prevCacheKey)) {
        console.log('🔄 [VERSION] Pre-cargando versión anterior:', prevIndex);
        switchVersion(user.uid, currentChatId, baseMessageId, prevIndex)
          .then(result => {
            versionsCacheRef.current.set(prevCacheKey, {
              activeBranch: result.activeBranch,
              context: result.context
            });
            console.log('✅ [VERSION] Versión anterior pre-cargada:', prevIndex);
          })
          .catch(err => console.error('[VERSION] Error pre-cargando versión anterior:', err));
      }
    }

    // Pre-cargar versión siguiente (si existe)
    if (currentIndex < totalVersions - 1) {
      const nextIndex = currentIndex + 1;
      const nextCacheKey = `${currentChatId}-${baseMessageId}-${nextIndex}`;

      if (!versionsCacheRef.current.has(nextCacheKey)) {
        console.log('🔄 [VERSION] Pre-cargando versión siguiente:', nextIndex);
        switchVersion(user.uid, currentChatId, baseMessageId, nextIndex)
          .then(result => {
            versionsCacheRef.current.set(nextCacheKey, {
              activeBranch: result.activeBranch,
              context: result.context
            });
            console.log('✅ [VERSION] Versión siguiente pre-cargada:', nextIndex);
          })
          .catch(err => console.error('[VERSION] Error pre-cargando versión siguiente:', err));
      }
    }
  }, [user, currentChatId]);

  /**
   * Cambia a una versión diferente de un mensaje
   */
  const handleSwitchVersion = useCallback(async (baseMessageId, versionIndex, totalVersions = 0) => {
    if (!user || !currentChatId) {
      return;
    }

    try {
      // Crear clave de cache para esta versión
      const cacheKey = `${currentChatId}-${baseMessageId}-${versionIndex}`;

      // OPTIMISTIC UPDATE: Si tenemos esta versión en cache, mostrarla inmediatamente
      const cachedVersion = versionsCacheRef.current.get(cacheKey);
      if (cachedVersion) {
        console.log('📦 [VERSION] Usando versión cacheada (instantánea):', cacheKey);

        // Actualizar UI inmediatamente desde cache
        setMessages(cachedVersion.activeBranch);
        setVisibleMessages(cachedVersion.activeBranch);
        setChatContext(cachedVersion.context);

        if (scrollToBottom) {
          requestAnimationFrame(() => {
            scrollToBottom({ force: true });
          });
        }

        // Pre-cargar versiones adyacentes en segundo plano
        if (totalVersions > 0) {
          setTimeout(() => {
            preloadAdjacentVersions(baseMessageId, versionIndex, totalVersions);
          }, 100);
        }

        // Refrescar en segundo plano por si hay actualizaciones (sin bloquear)
        switchVersion(user.uid, currentChatId, baseMessageId, versionIndex)
          .then(result => {
            versionsCacheRef.current.set(cacheKey, {
              activeBranch: result.activeBranch,
              context: result.context
            });
            setMessages(result.activeBranch);
            setVisibleMessages(result.activeBranch);
            setChatContext(result.context);
          })
          .catch(err => console.error('[VERSION] Error refrescando cache:', err));

        return;
      }

      // Si no está en cache, hacer la llamada ASÍNCRONA sin bloquear la UI
      console.log('🌐 [VERSION] Obteniendo versión del servidor (en segundo plano):', cacheKey);

      // OPTIMISTIC UPDATE: Limpiar mensajes inmediatamente y mostrar estado de carga
      setMessages([]);
      setVisibleMessages([]);

      // Mostrar indicador de carga inmediatamente
      dispatch({ type: 'START_VERSION_LOAD' });

      // Hacer la llamada sin await para no bloquear
      switchVersion(user.uid, currentChatId, baseMessageId, versionIndex)
        .then(result => {
          // Guardar en cache para futuras navegaciones
          versionsCacheRef.current.set(cacheKey, {
            activeBranch: result.activeBranch,
            context: result.context
          });

          // Limitar tamaño del cache a 30 versiones más recientes
          if (versionsCacheRef.current.size > 30) {
            const firstKey = versionsCacheRef.current.keys().next().value;
            versionsCacheRef.current.delete(firstKey);
          }

          // Actualizar mensajes con la rama activa cuando llegue la respuesta
          setMessages(result.activeBranch);
          setVisibleMessages(result.activeBranch);
          setChatContext(result.context);

          // Ocultar indicador de carga
          dispatch({ type: 'END_VERSION_LOAD' });

          // Scroll al final
          if (scrollToBottom) {
            requestAnimationFrame(() => {
              scrollToBottom({ force: true });
            });
          }

          // Pre-cargar versiones adyacentes después de cargar la actual
          if (totalVersions > 0) {
            setTimeout(() => {
              preloadAdjacentVersions(baseMessageId, versionIndex, totalVersions);
            }, 200);
          }
        })
        .catch(error => {
          console.error('[VERSION] ❌ Error cargando versión:', error);

          // Ocultar indicador de carga incluso en caso de error
          dispatch({ type: 'END_VERSION_LOAD' });

          dispatch({
            type: 'SET_ERROR',
            payload: { message: 'Failed to switch version' }
          });
        });

    } catch (error) {
      console.error('[VERSION] ❌ Error cambiando versión:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: { message: 'Failed to switch version' }
      });
    }
  }, [user, currentChatId, setMessages, setVisibleMessages, setChatContext, scrollToBottom, preloadAdjacentVersions]);

  return {
    // Estados de espera y procesamiento
    isWaitingForResponse,
    isWaitingForServerResponse, // Nuevo estado exportado
    isStreaming, // Nuevo estado para indicar si está streaming
    isLoadingVersion, // Estado para indicar si se está cargando una versión
    waitingStartTime,
    isProcessingAudio,
    isProcessingImage,
    messageTypeInfo,
    activeTools, // Estado de tools en ejecución
    
    // Estados de mensajes
    lastMessageId,
    lastImageRequestId,
    lastAssistantMessage,
    ultraThoughts,
    
    // Estados de error y límite
    error,
    setError,
    retryAction,
    setRetryAction,
    rateLimitInfo,
    
    // Estados AGTMaker
    isAGTMakerOpen,
    currentAGTContent,
    
    // Métodos de manejo de mensajes
    handleSendMessage,
    handleNewChatInput,
    handleUpdateMessage,
    stopStreaming, // Nuevo método para detener streaming

    // Métodos de edición y versionado
    handleEditMessage,
    handleResendMessage,
    handleSwitchVersion,

    // Métodos AGTMaker
    handleOpenAGTMaker,
    handleCloseAGTMaker,
    handleAGTMakerContentReady,
    hasAGTTag,
    
    // Utilidades
    handleRetry,
    
    // Referencias
    userScrollingRef,
    autoScrollActive,
    setAutoScrollActive
  };
}
